import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Check, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel } from "@/components/app/PageParts";
import { api, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

const money = (c) => `$${(c / 100).toFixed(0)}`;

const PlanCard = ({ p, current, busy, onChoose, hasSub }) => (
  <div data-testid={`plan-card-${p.id}`} className={`rounded-xl border bg-white p-5 shadow-sm ${current ? "border-gold ring-1 ring-gold" : ""}`}>
    <p className="font-display text-lg font-semibold text-navy">{p.name}</p>
    <p className="mt-1 text-2xl font-bold text-navy">{p.price_cents ? `${money(p.price_cents)}/month` : "Free"}</p>
    <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
      <li className="flex gap-2"><Check className="h-4 w-4 text-gold-dark" /> All workspaces, documents, forms and reminders</li>
      <li className="flex gap-2"><Check className="h-4 w-4 text-gold-dark" /> {p.doc_reads} AI document readings / month</li>
      <li className="flex gap-2"><Check className="h-4 w-4 text-gold-dark" /> {p.form_help} AI form-help questions / month</li>
    </ul>
    {current ? <p className="mt-5 text-sm font-medium text-emerald-700" data-testid={`plan-current-${p.id}`}>Your current plan</p>
      : p.price_cents > 0 && !hasSub && <Button onClick={() => onChoose(p.id)} disabled={busy} data-testid={`plan-subscribe-${p.id}`} className="mt-5 w-full bg-navy hover:bg-navy-2">{busy ? "Opening checkout..." : `Subscribe to ${p.name}`}</Button>}
  </div>
);

export default function Billing() {
  const { workspace } = useOutletContext();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/billing").then(({ data: d }) => setData(d)).catch((e) => toast.error(errorMessage(e))); }, []);
  useEffect(load, [load]);

  const choose = async (plan) => {
    setBusy(true);
    try { const { data: d } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin }); window.location.href = d.checkout_url; }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  const manage = async () => {
    try { const { data: d } = await api.post("/billing/portal", { return_url: `${window.location.origin}/app/w/${workspace.id}/billing` }); window.location.href = d.url; }
    catch (e) { toast.error(errorMessage(e)); }
  };
  if (!data) return null;
  const sub = data.subscription;
  const hasSub = data.plan !== "free";
  const limits = data.plans.find((p) => p.id === data.plan);
  return (
    <div data-testid="billing-page">
      <PageHeader eyebrow="Account" title="Plan & billing" subtitle="One subscription covers all the workspaces you own. Members of your workspaces use your plan's AI limits there." />
      {data.mode === "test" && <p data-testid="billing-test-mode" className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">Test mode: no real money is charged. Use card 4242 4242 4242 4242 with any future date and any CVC. {data.pricing_note}</p>}
      <Panel title="This month's AI usage" testId="billing-usage-panel">
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <p className="text-sm text-slate-600" data-testid="billing-usage-doc-reads"><span className="block font-display text-2xl font-bold text-navy">{data.usage.doc_reads} / {limits.doc_reads}</span>AI document readings</p>
          <p className="text-sm text-slate-600" data-testid="billing-usage-form-help"><span className="block font-display text-2xl font-bold text-navy">{data.usage.form_help} / {limits.form_help}</span>AI form-help questions</p>
        </div>
      </Panel>
      {sub && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border bg-white p-5" data-testid="billing-subscription">
          <CreditCard className="h-5 w-5 text-navy" />
          <p className="flex-1 text-sm text-slate-700"><strong className="text-navy">Status: {sub.status}</strong>{sub.current_period_end && ` · ${sub.cancel_at_period_end ? "Ends" : "Renews"} ${formatDate(sub.current_period_end)}`}</p>
          <Button variant="outline" onClick={manage} data-testid="billing-manage-button">Manage billing</Button>
        </div>
      )}
      <div className="mt-6 grid gap-4 md:grid-cols-3">{data.plans.map((p) => <PlanCard key={p.id} p={p} current={p.id === data.plan} busy={busy} onChoose={choose} hasSub={hasSub} />)}</div>
      <p className="mt-4 text-xs text-slate-500">Prices are in US dollars. {data.tax_note} Government filing fees are never included. Cancel any time from Manage billing.</p>
    </div>
  );
}
